import { FC, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Package, X, Check, Calendar, RefreshCw } from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import CustomModal from "../../../components/common/custom-modal/custom-modal";
import { pxToRem } from "../../../util";
import { formatDate } from "../../../util/common-functions";
import { IBoxStorageHistoryRow } from "../../../typings/interfaces/responses/boxStorageResponse";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { renewBoxStorageAction } from "../../../redux/actions/boxStorageAction";

dayjs.extend(utc);

interface IRenewBoxStorageModalProps {
  open: boolean;
  onClose: () => void;
  boxes: IBoxStorageHistoryRow[];
  onRenewSuccess?(): void;
}

const PERIODS = [
  { id: "6" as const, title: "6 Months", subtitle: "Standard renewal", price: 40 },
  {
    id: "12" as const,
    title: "1 Year",
    subtitle: "Extended storage",
    price: 75,
    recommended: true,
  },
];

type Period = (typeof PERIODS)[number];

function PeriodCard({
  item,
  selected,
  onClick,
}: {
  item: Period;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "relative min-h-[76px] rounded-2xl border px-3.5 py-2.5 text-left transition-all duration-150",
        selected
          ? "border-[#0079C2] bg-[#0079C2] text-white shadow-md"
          : "border-[#D9D9D9] bg-white text-[#515151] hover:border-[#0079C2]/40 hover:shadow-sm",
      ].join(" ")}
    >
      {selected && (
        <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-white text-[#0079C2]">
          <Check size={12} strokeWidth={3} />
        </span>
      )}
      <div className="pr-6 text-sm font-bold">{item.title}</div>
      <div
        className={
          selected
            ? "mt-1 text-[11px] font-medium text-white/85"
            : "mt-1 text-[11px] font-medium text-[#515151]/60"
        }
      >
        {item.subtitle}
      </div>
      <div
        className={
          selected
            ? "mt-1.5 text-base font-extrabold text-white"
            : "mt-1.5 text-base font-extrabold text-[#018752]"
        }
      >
        ${item.price.toFixed(2)}
      </div>
      {item.recommended && (
        <div
          className={
            selected
              ? "mt-1.5 inline-flex rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold"
              : "mt-1.5 inline-flex rounded-full bg-[#018752]/10 px-2 py-0.5 text-[10px] font-bold text-[#018752]"
          }
        >
          Best value
        </div>
      )}
    </button>
  );
}

function InfoPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-white px-3 py-2.5 shadow-sm ring-1 ring-[#D9D9D9]/70">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#515151]/50">
        {label}
      </div>
      <div className="mt-1 text-sm font-extrabold text-[#515151]">{value}</div>
    </div>
  );
}

const RenewBoxStorageModal: FC<IRenewBoxStorageModalProps> = ({
  open,
  onClose,
  boxes,
  onRenewSuccess,
}) => {
  const snackbar = useSnackbarContext();
  const dispatch = useAppDispatch();
  const { renewBoxStorageLoading } = useAppSelector(
    (state) => state.boxStorage
  );

  const [selectedPeriod, setSelectedPeriod] = useState<"6" | "12">("6");
  const [automaticRenewal, setAutomaticRenewal] = useState(true);

  const period = useMemo(
    () => PERIODS.find((p) => p.id === selectedPeriod) ?? PERIODS[0],
    [selectedPeriod]
  );

  const totalInvoices = useMemo(
    () => boxes.reduce((sum, box) => sum + (box.invoices?.length ?? 0), 0),
    [boxes]
  );

  const totalPieces = useMemo(
    () => boxes.reduce((sum, box) => sum + (box.pieces ?? 0), 0),
    [boxes]
  );

  const earliestEndDate = useMemo(() => {
    const dates = boxes.map((box) => box.dueDate).filter(Boolean) as string[];
    if (dates.length === 0) return null;
    return dates.reduce((earliest, current) =>
      new Date(current) < new Date(earliest) ? current : earliest
    );
  }, [boxes]);

  const newExpiryIso = useMemo(() => {
    const months = selectedPeriod === "6" ? 6 : 12;
    return dayjs().utc().add(months, "month").toISOString();
  }, [selectedPeriod]);

  const total = period.price * boxes.length;

  const handleRenew = async () => {
    try {
      await dispatch(
        renewBoxStorageAction({
          configIds: boxes.map((box) => box.configId),
          storageExpiryDate: newExpiryIso,
        })
      ).unwrap();
      snackbar.showSnackbar(
        `${boxes.length} box${boxes.length === 1 ? "" : "es"} renewed for ${
          period.title
        }`,
        "success"
      );
      onRenewSuccess?.();
      onClose();
    } catch (err) {
      snackbar.showSnackbar("Failed to renew box storage", "error");
    }
  };

  return (
    <CustomModal
      openModal={open}
      onClose={onClose}
      title=""
      hideCloseButton
      mode="popup"
      width={pxToRem(600)}
      height={pxToRem(780)}
      sx={{ padding: 0, borderRadius: "1.5rem", overflow: "hidden" }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="flex items-start justify-between px-8 pb-4 pt-7">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0079C2]/10 text-[#0079C2]">
              <Package size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-[#515151]">
                Renew Box Storage
              </h1>
              <p className="mt-1 text-sm font-medium text-[#515151]/60">
                Extend selected storage boxes and confirm renewal charge
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D9D9D9] text-[#515151] transition hover:bg-[#D9D9D9]/35"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 pb-7">
          <div className="rounded-3xl bg-[#F4F4F4] p-4">
            <div className="mb-3 grid grid-cols-3 gap-3">
              <InfoPill
                label="Selected boxes"
                value={`${boxes.length} box${boxes.length === 1 ? "" : "es"}`}
              />
              <InfoPill
                label="Total invoices"
                value={`${totalInvoices} invoice${
                  totalInvoices === 1 ? "" : "s"
                }`}
              />
              <InfoPill label="Total pieces" value={`${totalPieces} pieces`} />
            </div>

            <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-[#D9D9D9]/60">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-extrabold text-[#515151]">
                    Boxes Being Renewed
                  </h3>
                  <p className="mt-0.5 text-[11px] font-medium text-[#515151]/55">
                    Current expiration will be extended based on the renewal
                    period
                  </p>
                </div>
                {earliestEndDate && (
                  <span className="shrink-0 rounded-full bg-[#930000]/10 px-3 py-1 text-[11px] font-bold text-[#930000]">
                    Expires {formatDate(earliestEndDate)}
                  </span>
                )}
              </div>

              <div className="space-y-2">
                {boxes.map((box) => (
                  <div
                    key={box.configId}
                    className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-2xl border border-[#D9D9D9] bg-white px-3.5 py-2.5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#0079C2]/10 text-[#0079C2]">
                        <Package size={18} />
                      </div>
                      <div>
                        <div className="text-sm font-extrabold text-[#515151]">
                          {box.boxNo}
                        </div>
                        <div className="text-xs font-medium text-[#515151]/55">
                          {box.invoices?.length ?? 0} invoices ·{" "}
                          {box.pieces ?? 0} pieces
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] font-semibold text-[#515151]/50">
                        Current end
                      </div>
                      <div className="text-sm font-extrabold text-[#515151]">
                        {formatDate(box.dueDate)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-[#D9D9D9]/60">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-extrabold text-[#515151]">
                  Renewal Period
                </h3>
                <span className="text-[11px] font-semibold text-[#515151]/55">
                  Applies to all selected boxes
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {PERIODS.map((p) => (
                  <PeriodCard
                    key={p.id}
                    item={p}
                    selected={selectedPeriod === p.id}
                    onClick={() => setSelectedPeriod(p.id)}
                  />
                ))}
              </div>
            </section>
          </div>

          <div className="mt-4 rounded-2xl border border-[#D9D9D9] bg-white px-4 py-3">
            <div className="flex items-start justify-between gap-5">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-xl bg-[#018752]/10 text-[#018752]">
                  <Calendar size={18} />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-[#515151]/50">
                    Renewal Summary
                  </div>
                  <div className="mt-1 text-sm font-bold text-[#515151]">
                    {boxes.length} box{boxes.length === 1 ? "" : "es"} ·{" "}
                    {period.title}
                  </div>
                  <div className="mt-1 text-xs font-medium text-[#515151]/55">
                    New expiration date:{" "}
                    <span className="font-extrabold text-[#515151]">
                      {formatDate(newExpiryIso)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-[#515151]/55">
                  Renewal charge
                </div>
                <div className="text-2xl font-extrabold text-[#018752]">
                  ${total.toFixed(2)}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setAutomaticRenewal((value) => !value)}
              className="mt-3 flex w-full items-center justify-between rounded-2xl border border-[#D9D9D9] bg-[#F8F8F8] px-4 py-3 text-left transition hover:bg-[#F4F4F4]"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white text-[#0079C2] ring-1 ring-[#D9D9D9]">
                  <RefreshCw size={16} />
                </div>
                <div>
                  <div className="text-sm font-extrabold text-[#515151]">
                    Automatic renewal
                  </div>
                  <div className="text-xs font-medium text-[#515151]/55">
                    Renew this storage automatically when the selected period
                    ends
                  </div>
                </div>
              </div>
              <span
                className={[
                  "flex h-7 w-12 items-center rounded-full p-1 transition",
                  automaticRenewal ? "bg-[#018752]" : "bg-[#D9D9D9]",
                ].join(" ")}
              >
                <span
                  className={[
                    "h-5 w-5 rounded-full bg-white shadow transition",
                    automaticRenewal ? "translate-x-5" : "translate-x-0",
                  ].join(" ")}
                />
              </span>
            </button>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={onClose}
              disabled={renewBoxStorageLoading}
              className="h-12 rounded-2xl border-2 border-[#515151]/20 bg-white text-base font-extrabold text-[#515151]/60 transition hover:bg-[#D9D9D9]/25 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRenew}
              disabled={boxes.length === 0 || renewBoxStorageLoading}
              className="flex h-12 items-center justify-center gap-2 rounded-2xl bg-[#018752] text-base font-extrabold text-white shadow-lg shadow-green-200 transition hover:opacity-95 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {renewBoxStorageLoading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Renew Storage"
              )}
            </button>
          </div>
        </div>
      </Box>
    </CustomModal>
  );
};

export default RenewBoxStorageModal;
